
import React, { createContext, useState, useEffect, ReactNode } from 'react';
import { User, PurchaseHistory, AuthContextType } from '@/types/user';
import { supabase } from '@/integrations/supabase/client';
import { useLogin } from '@/hooks/auth/useLogin';
import { useAuthRegister } from '@/hooks/auth/useAuthRegister';
import { usePasswordReset } from '@/hooks/auth/usePasswordReset';
import { usePurchaseHistory } from '@/hooks/auth/usePurchaseHistory';
import { useUserCredit } from '@/hooks/auth/useUserCredit';
import { useUpdateProfile } from '@/hooks/auth/useUpdateProfile';

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [purchaseHistory, setPurchaseHistory] = useState<PurchaseHistory[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize hooks with proper parameters
  const { login } = useLogin(setUser, setPurchaseHistory, setIsLoading);
  const { register } = useAuthRegister(setIsLoading);
  const { resetPassword } = usePasswordReset(setIsLoading);
  const purchaseHistoryHook = usePurchaseHistory(user);
  const { addUserCredit, useCredit } = useUserCredit(user, setUser);
  const { updateUserName } = useUpdateProfile(user, setUser);

  const logout = () => {
    supabase.auth.signOut();
    setUser(null);
    setPurchaseHistory([]);
  };

  const createUserFromSession = async (session: any) => {
    try {
      // Create basic user from session data
      const basicUser: User = {
        id: session.user.id,
        email: session.user.email || '',
        name: session.user.user_metadata?.name || session.user.email?.split('@')[0] || 'User',
        createdAt: new Date(session.user.created_at),
        credit: 0
      };
      
      // Set user immediately for fast UI update
      setUser(basicUser);
      
      // Fetch or create profile to get accurate credit
      try {
        // First, try to get existing profile
        const { data: existingProfile, error: fetchError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', session.user.id)
          .maybeSingle();
        
        if (existingProfile) {
          // Parse credit properly - handle both string and number formats
          let creditValue = 0;
          if (existingProfile.credit !== null && existingProfile.credit !== undefined) {
            if (typeof existingProfile.credit === 'string') {
              creditValue = parseFloat(existingProfile.credit) || 0;
            } else if (typeof existingProfile.credit === 'number') {
              creditValue = existingProfile.credit;
            }
          }
          
          setUser(prev => prev ? { 
            ...prev, 
            name: existingProfile.name || prev.name, 
            credit: creditValue
          } : null);
        } else {
          // Create profile if it doesn't exist
          const { data: newProfile, error: insertError } = await supabase
            .from('profiles')
            .insert({
              id: session.user.id,
              name: basicUser.name,
              credit: 0
            })
            .select()
            .maybeSingle();
          
          if (!insertError) {
            setUser(prev => prev ? { 
              ...prev, 
              credit: 0
            } : null);
          }
        }
      } catch (profileError) {
        // Silent fail for profile operations
      }
      
      return basicUser;
    } catch (error) {
      return {
        id: session.user.id,
        email: session.user.email || '',
        name: session.user.user_metadata?.name || 'User',
        createdAt: new Date(session.user.created_at),
        credit: 0
      };
    }
  };

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (session?.user) {
        await createUserFromSession(session);
      }
      setIsLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        // Use setTimeout to defer async operations
        setTimeout(() => {
          if (event === 'SIGNED_IN' && session?.user) {
            createUserFromSession(session);
          } else if (event === 'SIGNED_OUT') {
            setUser(null);
            setPurchaseHistory([]);
          }
          setIsLoading(false);
        }, 0);
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Load purchase history when user is authenticated
  useEffect(() => {
    const loadUserPurchaseHistory = async () => {
      if (user && user.id && !isLoading) {
        try {
          await purchaseHistoryHook.loadPurchaseHistory(user.id);
        } catch (error) {
          // Silent fail
        }
      }
    };

    // Load purchase history when user changes and loading is complete
    if (user && !isLoading) {
      // Small delay to ensure session is established
      setTimeout(() => {
        loadUserPurchaseHistory();
      }, 100);
    }

    // Listen for custom event from Profile page
    const handleLoadPurchaseHistory = (event: CustomEvent) => {
      const { userId } = event.detail;
      if (userId && user?.id === userId) {
        loadUserPurchaseHistory();
      }
    };

    window.addEventListener('load-purchase-history', handleLoadPurchaseHistory as EventListener);

    return () => {
      window.removeEventListener('load-purchase-history', handleLoadPurchaseHistory as EventListener);
    };
  }, [user?.id, isLoading]); // Depend on both user.id and isLoading

  // Sync the hook's purchase history with the AuthProvider's state
  useEffect(() => {
    setPurchaseHistory(purchaseHistoryHook.purchaseHistory);
  }, [purchaseHistoryHook.purchaseHistory]);

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    register,
    resetPassword,
    logout,
    purchaseHistory,
    addPurchaseToHistory: purchaseHistoryHook.addPurchaseToHistory,
    addUserCredit,
    useCredit,
    updateUserName,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
